import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useMailAlerts } from "../lib/mailAlerts";
import { StaffChrome } from "../components/StaffChrome";
import { ReportsQueue, ListingsModeration, PendingQueue, StaffConversations, useStaffUnread } from "../components/StaffPanels";
import { ContactInbox } from "../components/ContactInbox";
import { StaffMail } from "../components/staff-mail/StaffMail";
import { Tabs, TabsContent } from "../components/ui/tabs";
import { TickerSettings } from "./admin/TickerSettings";
import { HomepageSettings } from "./admin/HomepageSettings";
import { AdminCreateListing } from "./admin/AdminCreate";
import { Overview, FeaturedPerformance, ActivityLog } from "./admin/AdminDashboard";
import { VerificationQueue } from "./admin/VerificationQueue";
import { UsersManager } from "./admin/UsersManager";
import { Announce } from "./admin/Announce";
import { AppealsQueue } from "./admin/AppealsQueue";
import { BackupTab } from "./admin/BackupTab";
import { WatermarkSettings } from "./admin/WatermarkSettings";
import { FlyerTerms } from "./admin/FlyerTerms";
import { SearchNicknames } from "./admin/SearchNicknames";
import { BreederBoostToggle } from "./admin/BreederBoostToggle";
import { LitterRulesToggle } from "./admin/LitterRulesToggle";
import { SubscribersTab } from "./admin/SubscribersTab";
import { SiteMapTab } from "./admin/sitemap/SiteMapTab";
import { SampleAdsImport } from "./admin/SampleAdsImport";
import { AdminMenu, Dot } from "./admin/menu/AdminMenu";
import {
  Megaphone, Bell, Radio, Download, Home, Languages, Tags, Users, Stamp, Network,
  LayoutDashboard, Clock, Flag, Scale, List, PlusSquare, BadgeCheck, Activity, Inbox, Mail, MessagesSquare,
} from "lucide-react";

const DEF_TOP = ["announce", "ticker", "homepage", "flyer-terms", "subscribers", "nicknames", "watermark", "backup", "sitemap", "ad-manager"];
const DEF_TABS = ["overview", "pending", "reports", "appeals", "listings", "create", "users", "verification", "activity", "contact", "email", "conversations"];

const OnlineDot = ({ n }) => (n > 0 ? (
  <span data-testid="users-online-count" title={`${n} user${n === 1 ? "" : "s"} online now`} className="ml-1.5 inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded-full h-5 px-1.5">
    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />{n}
  </span>
) : null);

export default function AdminPanel() {
  const [tab, setTab] = useState("overview");
  const unread = useStaffUnread();
  const [appealsOpen, setAppealsOpen] = useState(0);
  const [contactNew, setContactNew] = useState(0);
  const [replyUnread, setReplyUnread] = useState(0);
  const [onlineUsers, setOnlineUsers] = useState(0);
  const [openMail, setOpenMail] = useState(null);
  const [mailUnread, setMailUnread] = useMailAlerts((id) => { setTab("email"); setOpenMail({ id, at: Date.now() }); });
  useEffect(() => {
    const load = () => {
      api.get("/admin/appeals").then((r) => setAppealsOpen(r.data.open_count || 0)).catch(() => {});
      api.get("/admin/contact-messages?status=new").then((r) => setContactNew(r.data.new_count || 0)).catch(() => {});
      api.get("/admin/users/unread-replies").then((r) => setReplyUnread(r.data.total || 0)).catch(() => {});
      api.get("/admin/presence").then((r) => setOnlineUsers(r.data.online_users || 0)).catch(() => {});
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);
  const items = {
    announce: { label: "Announce", icon: Bell, testid: "admin-btn-announce" },
    ticker: { label: "Ticker", icon: Radio, testid: "admin-btn-ticker" },
    homepage: { label: "Homepage", icon: Home, testid: "admin-btn-homepage" },
    "flyer-terms": { label: "Flyer FR", icon: Languages, testid: "admin-btn-flyer-terms" },
    subscribers: { label: "Subscribers", icon: Users, testid: "admin-btn-subscribers" },
    nicknames: { label: "Nicknames", icon: Tags, testid: "admin-btn-nicknames" },
    watermark: { label: "Watermark", icon: Stamp, testid: "admin-btn-watermark" },
    backup: { label: "Backup", icon: Download, testid: "admin-btn-backup" },
    sitemap: { label: "Site Map", icon: Network, testid: "admin-btn-sitemap" },
    "ad-manager": { label: "Ad Manager", icon: Megaphone, testid: "admin-ads-link", to: "/admin/ads" },
    overview: { label: "Overview", icon: LayoutDashboard, testid: "admin-tab-overview" },
    pending: { label: "Pending", icon: Clock, testid: "admin-tab-pending" },
    reports: { label: "Reports", icon: Flag, testid: "admin-tab-reports" },
    appeals: { label: "Appeals", icon: Scale, testid: "admin-tab-appeals", badges: <Dot n={appealsOpen} testid="appeals-open-dot" /> },
    listings: { label: "Listings", icon: List, testid: "admin-tab-listings" },
    create: { label: "Create", icon: PlusSquare, testid: "admin-tab-create" },
    users: { label: "Users", icon: Users, testid: "admin-tab-users", badges: <><OnlineDot n={onlineUsers} /><Dot n={replyUnread} testid="users-reply-unread-dot" /></> },
    verification: { label: "Verification", icon: BadgeCheck, testid: "admin-tab-verification" },
    activity: { label: "Activity", icon: Activity, testid: "admin-tab-activity" },
    contact: { label: "Contact", icon: Inbox, testid: "admin-tab-contact", badges: <Dot n={contactNew} testid="contact-new-dot" /> },
    email: { label: "Email", icon: Mail, testid: "admin-tab-email", badges: <Dot n={mailUnread} testid="email-unread-dot" /> },
    conversations: { label: "Conversations", icon: MessagesSquare, testid: "admin-tab-conversations", badges: <Dot n={unread} testid="conversations-unread-dot" /> },
  };
  return (
    <StaffChrome title="Admin Panel">
      <Tabs value={tab} onValueChange={setTab}>
        <AdminMenu items={items} defTop={DEF_TOP} defTabs={DEF_TABS} tab={tab} setTab={setTab} />
        <TabsContent value="overview"><Overview onNavigate={setTab} /><FeaturedPerformance /></TabsContent>
        <TabsContent value="pending"><PendingQueue /></TabsContent>
        <TabsContent value="reports"><ReportsQueue /></TabsContent>
        <TabsContent value="appeals"><AppealsQueue /></TabsContent>
        <TabsContent value="listings"><ListingsModeration /></TabsContent>
        <TabsContent value="create"><AdminCreateListing /></TabsContent>
        <TabsContent value="users"><UsersManager /></TabsContent>
        <TabsContent value="verification"><LitterRulesToggle /><BreederBoostToggle /><VerificationQueue /></TabsContent>
        <TabsContent value="activity"><ActivityLog /></TabsContent>
        <TabsContent value="announce"><Announce /></TabsContent>
        <TabsContent value="ticker"><TickerSettings /></TabsContent>
        <TabsContent value="homepage"><HomepageSettings /></TabsContent>
        <TabsContent value="backup"><SampleAdsImport /><BackupTab /></TabsContent>
        <TabsContent value="sitemap"><SiteMapTab /></TabsContent>
        <TabsContent value="watermark"><WatermarkSettings /></TabsContent>
        <TabsContent value="flyer-terms"><FlyerTerms /></TabsContent>
        <TabsContent value="nicknames"><SearchNicknames /></TabsContent>
        <TabsContent value="subscribers"><SubscribersTab /></TabsContent>
        <TabsContent value="contact"><ContactInbox onCountChange={setContactNew} /></TabsContent>
        <TabsContent value="email"><StaffMail onCountChange={setMailUnread} openRequest={openMail} liveUnread={mailUnread} /></TabsContent>
        <TabsContent value="conversations"><StaffConversations /></TabsContent>
      </Tabs>
    </StaffChrome>
  );
}
